import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Divider,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import TuneIcon from "@mui/icons-material/Tune";
import ProductsHeader from "../../../NewProductList/components/ProductsHeader/ProductsHeader";

const METHOD_COLORS = {
  GET: "info",
  POST: "success",
  PUT: "warning",
};

const AUTH_COLORS = {
  none: "default",
  basic: "primary",
  bearer: "secondary",
  api_key: "warning",
  oauth2_client_credentials: "success",
  oauth2_with_extra_header: "success",
};

const AUTH_LABELS = {
  none: "No Auth",
  basic: "Basic",
  bearer: "Bearer",
  api_key: "API Key",
  oauth2_client_credentials: "OAuth2",
  oauth2_with_extra_header: "OAuth2 + Header",
};

function ChannelCard(props) {
  var channel = props.channel;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onTest = props.onTest;

  var authType = (channel.auth && channel.auth.type) || "none";
  var callbackEnabled = !!(
    channel.callback && channel.callback.enabled
  );
  var endpointDisplay = channel.endpoint
    ? channel.endpoint.length > 50
      ? channel.endpoint.substring(0, 50) + "..."
      : channel.endpoint
    : "—";

  return (
    <Card
      variant="outlined"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 8,
        transition: "box-shadow 0.2s",
      }}
    >
      <CardContent style={{ flexGrow: 1, paddingBottom: 4 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Chip
            size="small"
            label={channel.method || "POST"}
            color={METHOD_COLORS[channel.method] || "default"}
          />
          <Chip
            size="small"
            label={AUTH_LABELS[authType] || authType}
            color={AUTH_COLORS[authType] || "default"}
            variant="outlined"
          />
          {callbackEnabled && (
            <Chip
              size="small"
              label="Callback"
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {channel.channel_name || "(default)"}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          style={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            wordBreak: "break-all",
          }}
        >
          {endpointDisplay}
        </Typography>

        {channel.phone_format && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Phone format:{" "}
              <strong>{channel.phone_format}</strong>
            </Typography>
          </Box>
        )}

        {channel.callback && channel.callback.enabled && (
          <Box mt={0.5}>
            <Typography variant="caption" color="text.secondary">
              Callback field:{" "}
              <strong>
                {channel.callback.status_field || "—"}
              </strong>
            </Typography>
          </Box>
        )}
      </CardContent>

      <Divider />

      <CardActions style={{ justifyContent: "flex-end", padding: "4px 8px" }}>
        <Tooltip title="Test this channel">
          <IconButton
            size="small"
            color="primary"
            onClick={function() { onTest(channel); }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit channel">
          <IconButton
            size="small"
            color="default"
            onClick={function() { onEdit(channel); }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete channel">
          <IconButton
            size="small"
            color="error"
            onClick={function() { onDelete(channel); }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

function ResolutionSection(props) {
  var resolution = props.resolution;
  if (!resolution) return null;

  return (
    <Paper
      variant="outlined"
      style={{ padding: 16, marginTop: 16, borderRadius: 8 }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="subtitle2" color="primary">
          Channel Resolution
        </Typography>
        <Tooltip title="Edit channel resolution">
          <IconButton size="small" onClick={props.onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Strategy
          </Typography>
          <Typography variant="body2">
            {resolution.strategy || "—"}
          </Typography>
        </Grid>
        {resolution.rules && resolution.rules.length > 0 && (
          <Grid item xs={12} sm={8}>
            <Typography variant="caption" color="text.secondary">
              Rules
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {resolution.rules.map(function(rule, i) {
                return (
                  <Chip
                    key={i}
                    size="small"
                    label={
                      (rule.prefix || rule.match || "*") +
                      " → " +
                      rule.channel
                    }
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </Grid>
        )}
        {resolution.default_channel && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Default channel:{" "}
              <strong>{resolution.default_channel}</strong>
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

export default function ViewPartnerSmsConfig(props) {
  var isLoading = props.isLoading;
  var channels = props.smsChannels || [];
  var resolution = props.smsChannelResolution;
  var onAddChannel = props.onAddChannel;
  var onEditChannel = props.onEditChannel;
  var onDeleteChannel = props.onDeleteChannel;
  var onTestChannel = props.onTestChannel;
  var onConfigureResolution = props.onConfigureResolution;

  var [expanded, setExpanded] = React.useState(false);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 32 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ marginTop: "3%" }}>
      <Accordion expanded={expanded}>
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              onClick={function() { setExpanded(!expanded); }}
            />
          }
          aria-controls="sms-config-content"
          id="sms-config-header"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            pr={2}
          >
            <ProductsHeader
              title={"Partner SMS Configurations"}
              showEditButton={false}
            />
            <Box display="flex" alignItems="center" gap={1}>
              {channels.length > 0 && (
                <Chip
                  size="small"
                  label={channels.length + " channel" + (channels.length !== 1 ? "s" : "")}
                  color="primary"
                  variant="outlined"
                />
              )}
              {channels.length > 1 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TuneIcon />}
                  onClick={function(e) {
                    e.stopPropagation();
                    onConfigureResolution();
                  }}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Channel Resolution
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={function(e) {
                  e.stopPropagation();
                  onAddChannel();
                }}
                style={{ whiteSpace: "nowrap" }}
              >
                Add Channel
              </Button>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {channels.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
              color="text.secondary"
            >
              <Typography variant="body1" gutterBottom>
                No SMS channels configured yet.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAddChannel}
                style={{ marginTop: 8 }}
              >
                Add your first channel
              </Button>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {channels.map(function(channel, idx) {
                  var key =
                    channel.channel_name || "channel-" + idx;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      <ChannelCard
                        channel={channel}
                        onEdit={onEditChannel}
                        onDelete={onDeleteChannel}
                        onTest={onTestChannel}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              <ResolutionSection resolution={resolution} onEdit={onConfigureResolution} />
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <br />
    </div>
  );
}
